import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import type {
  ICircleConversation,
  IConversation,
  IConversationList,
  IFriendConversation,
  IFriendItem,
  IFriendRequestItem,
  IInvitationConversation
} from '@/service/friend';
import {
  EConversationType,
  getFriendListV2,
  getFriendRequestList,
  markMessagesAsRead
} from '@/service/friend';

const PAGE_SIZE = 20;

interface IFriendStore {
  conversationList: IConversationList | undefined;
  friendRequestList: IFriendRequestItem[];
  strangerList: IFriendItem[];
  unreadList: IConversationList | undefined;
  totalUnreadCount: number;
  // 分页相关
  friendListPage: number;
  unreadListPage: number;
  friendListHasMore: boolean;
  unreadListHasMore: boolean;
  // 方法
  updateFriendList: (params?: { pageNo?: number; pageSize?: number; isRefresh?: boolean }) => void;
  updateFriendItemById: (
    friendId: string,
    friendItem: Partial<IFriendConversation['friend']>
  ) => void;
  updateConversationItemBySession: (
    SessionId: string,
    conversationItem: Partial<IConversation>,
    chatHomepageRoute: string,
    isDelete?: boolean
  ) => void;
  updateFriendRequestList: () => void;
  updateUnreadList: (params?: { pageNo?: number; pageSize?: number; isRefresh?: boolean }) => void;
  updateStrangerItemBySession: (SessionId: string, friendItem: Partial<IFriendItem>) => void;
  deleteFriendById: (friendId: string) => void;
  updateFriendInvitation: (data: IInvitationConversation, isDelete?: boolean) => void;
  // TODO: 更新 chatCircleRoute 的接入
  updateFriendCircle: (data: ICircleConversation, isDelete?: boolean) => void;
  requestLoading: boolean;
  loading: boolean;
}

const useFriendStore = create<IFriendStore>()(
  immer((set, get) => ({
    conversationList: undefined as IConversationList | undefined,
    friendRequestList: [] as IFriendRequestItem[],
    strangerList: [] as IFriendItem[],
    unreadList: undefined as IConversationList | undefined,
    totalUnreadCount: 0,
    loading: true as boolean,
    requestLoading: true as boolean,
    // 分页相关
    friendListPage: 1,
    unreadListPage: 1,
    friendListHasMore: true as boolean,
    unreadListHasMore: true as boolean,
    deleteFriendById: (friendId: string) => {
      const foundIndex = get().conversationList?.conversations.findIndex(
        (item) =>
          item.conversationType === EConversationType.FRIEND &&
          (item as IFriendConversation).friend.id === friendId
      );

      if (foundIndex !== undefined && foundIndex > -1) {
        set((draft) => {
          draft.conversationList?.conversations.splice(foundIndex, 1);
        });
      }
    },
    // 本地更新Invitation信息
    updateFriendInvitation: (data, isDelete = false) => {
      set((draft) => {
        if (!draft.conversationList) {
          draft.conversationList = { conversations: [], total: 0, totalUnreadCount: 0 };
        }

        // 查找现有的 invitation conversation
        const invitationIndex = draft.conversationList.conversations.findIndex(
          (item) => item.conversationType === EConversationType.FRIEND_INVITATION
        );

        // 删除操作
        if (isDelete) {
          if (invitationIndex > -1) {
            const unreadCount =
              draft.conversationList.conversations[invitationIndex].unreadCount || 0;

            // 从 conversationList 删除
            draft.conversationList.conversations.splice(invitationIndex, 1);

            // 更新总未读数
            if (unreadCount > 0) {
              draft.totalUnreadCount = Math.max(0, (draft.totalUnreadCount || 0) - unreadCount);
            }

            // 从 unreadList 删除
            if (draft.unreadList?.conversations) {
              const deleteIndex = draft.unreadList.conversations.findIndex(
                (item) => item.conversationType === EConversationType.FRIEND_INVITATION
              );

              if (deleteIndex > -1) {
                draft.unreadList.conversations.splice(deleteIndex, 1);
                draft.unreadList.totalUnreadCount = Math.max(
                  0,
                  (draft.unreadList.totalUnreadCount || 0) - unreadCount
                );
              }
            }
          }

          return;
        }

        // 更新操作
        if (invitationIndex > -1) {
          draft.conversationList.conversations[invitationIndex] = data;
        } else {
          draft.conversationList.conversations.unshift({ ...data });
        }

        // 同步更新unreadList
        if (!draft.unreadList) {
          draft.unreadList = { conversations: [], total: 0, totalUnreadCount: 0 };
        }

        const unreadInvitationIndex = draft.unreadList.conversations.findIndex(
          (item) => item.conversationType === EConversationType.FRIEND_INVITATION
        );

        if (unreadInvitationIndex > -1) {
          // 计算未读数差值
          const prevUnread = draft.unreadList.conversations[unreadInvitationIndex].unreadCount || 0;
          const newUnread = data.unreadCount || 0;

          draft.unreadList.totalUnreadCount += newUnread - prevUnread;
          draft.totalUnreadCount += newUnread - prevUnread;
          draft.unreadList.conversations[unreadInvitationIndex] = data;
        } else if (data.unreadCount > 0) {
          draft.unreadList.conversations.unshift({ ...data });
          draft.unreadList.totalUnreadCount += data.unreadCount;
          draft.totalUnreadCount += data.unreadCount;
        }
      });
    },
    // 本地更新Circle信息
    updateFriendCircle: (data, isDelete = false) => {
      set((draft) => {
        if (!draft.conversationList) {
          draft.conversationList = { conversations: [], total: 0, totalUnreadCount: 0 };
        }

        const circleRoute = data.circle.route;

        const circleIndex = draft.conversationList.conversations.findIndex(
          (item) =>
            item.conversationType === EConversationType.CIRCLE &&
            (item as ICircleConversation).circle.route === circleRoute
        );

        // 删除操作
        if (isDelete) {
          if (circleIndex > -1) {
            const unreadCount = draft.conversationList.conversations[circleIndex].unreadCount || 0;

            // 从 conversationList 删除
            draft.conversationList.conversations.splice(circleIndex, 1);

            // 更新总未读数
            if (unreadCount > 0) {
              draft.totalUnreadCount = Math.max(0, (draft.totalUnreadCount || 0) - unreadCount);
            }

            // 从 unreadList 删除
            if (draft.unreadList?.conversations) {
              const deleteIndex = draft.unreadList.conversations.findIndex(
                (item) =>
                  item.conversationType === EConversationType.CIRCLE &&
                  (item as ICircleConversation).circle.route === circleRoute
              );

              if (deleteIndex > -1) {
                draft.unreadList.conversations.splice(deleteIndex, 1);
                draft.unreadList.totalUnreadCount = Math.max(
                  0,
                  (draft.unreadList.totalUnreadCount || 0) - unreadCount
                );
              }
            }
          }

          return;
        }

        // 更新操作
        if (circleIndex > -1) {
          draft.conversationList.conversations[circleIndex] = data;
        } else {
          draft.conversationList.conversations.unshift({ ...data });
        }

        // 同步更新unreadList
        if (!draft.unreadList) {
          draft.unreadList = { conversations: [], total: 0, totalUnreadCount: 0 };
        }

        const unreadCircleIndex = draft.unreadList.conversations.findIndex(
          (item) =>
            item.conversationType === EConversationType.CIRCLE &&
            (item as ICircleConversation).circle.route === circleRoute
        );

        if (unreadCircleIndex > -1) {
          // 计算未读数差值
          const prevUnread = draft.unreadList.conversations[unreadCircleIndex].unreadCount || 0;
          const newUnread = data.unreadCount || 0;

          draft.unreadList.totalUnreadCount += newUnread - prevUnread;
          draft.totalUnreadCount += newUnread - prevUnread;
          draft.unreadList.conversations[unreadCircleIndex] = data;
        } else if (data.unreadCount > 0) {
          draft.unreadList.conversations.unshift({ ...data });
          draft.unreadList.totalUnreadCount += data.unreadCount;
          draft.totalUnreadCount += data.unreadCount;
        }
      });
    },
    updateFriendList: (params) => {
      const { isRefresh = false } = params || {};
      const pageSize = PAGE_SIZE;
      const pageNo = isRefresh ? 1 : get().friendListPage;

      isRefresh && set({ friendListPage: 1 });

      set({ loading: true });
      getFriendListV2({ pageNo, pageSize })
        .then((res) => {
          if (res.data.code === 0) {
            const data = res.data.data;
            const total = data.total || 0;

            set((draft) => {
              if (pageNo === 1) {
                draft.conversationList = data;
              } else {
                const prevConversations = draft.conversationList?.conversations || [];

                draft.conversationList = {
                  conversations: [...prevConversations, ...(data.conversations || [])],
                  total,
                  totalUnreadCount: data.totalUnreadCount
                };
              }

              draft.friendListHasMore = (draft.conversationList.conversations.length || 0) < total;

              if (draft.friendListHasMore) {
                draft.friendListPage = pageNo + 1;
              }
            });
          }
        })
        .finally(() => {
          set({ loading: false });
        });
    },
    updateFriendItemById: (friendId, friendItem) => {
      set((draft) => {
        if (!draft.conversationList?.conversations) return;

        const foundIndex = draft.conversationList.conversations.findIndex(
          (item) =>
            item.conversationType === EConversationType.FRIEND &&
            (item as IFriendConversation).friend.id === friendId
        );

        if (foundIndex > -1) {
          const conversation = draft.conversationList.conversations[
            foundIndex
          ] as IFriendConversation;

          Object.assign(conversation.friend, friendItem);
        }
      });
    },
    updateConversationItemBySession: (
      sessionId,
      conversationItem,
      chatHomepageRoute,
      isDelete = false
    ) => {
      set((draft) => {
        if (!draft.conversationList?.conversations) return;

        // 1. 查找好友项
        const friendIndex = draft.conversationList.conversations.findIndex((item) => {
          if ('friend' in item) {
            return item.friend.sessionId === sessionId;
          }

          if ('circle' in item) {
            return item.circle.sessionId === sessionId;
          }

          return false;
        });

        // 删除操作
        if (isDelete) {
          if (friendIndex > -1) {
            const deleteConversation = draft.conversationList.conversations[friendIndex];
            const unreadCount = deleteConversation.unreadCount || 0;

            // 从 conversationList 删除
            draft.conversationList.conversations.splice(friendIndex, 1);

            // 更新总未读数
            if (unreadCount > 0) {
              draft.totalUnreadCount = Math.max(0, (draft.totalUnreadCount || 0) - unreadCount);
            }

            // 从 unreadList 删除
            if (draft.unreadList?.conversations) {
              const deleteUnreadIndex = draft.unreadList.conversations.findIndex((item) => {
                if ('friend' in item) {
                  return item.friend.sessionId === sessionId;
                }

                if ('circle' in item) {
                  return item.circle.sessionId === sessionId;
                }

                return false;
              });

              if (deleteUnreadIndex > -1) {
                draft.unreadList.conversations.splice(deleteUnreadIndex, 1);
                draft.unreadList.totalUnreadCount = Math.max(
                  0,
                  (draft.unreadList.totalUnreadCount || 0) - unreadCount
                );
              }
            }
          }

          return;
        }

        // 更新操作
        let actualFriendIndex = friendIndex;

        if (actualFriendIndex === -1) {
          // 确保 conversationItem 有完整的必需字段
          draft.conversationList.conversations.unshift(conversationItem as IConversation);
          actualFriendIndex = 0;
        }

        const conversation = draft.conversationList.conversations[actualFriendIndex] as any;
        const preUnreadCount = conversation?.unreadCount || 0;
        const conversationRoute = conversation?.friend?.route ?? conversation?.circle?.route;
        const isChat = conversationRoute === chatHomepageRoute;

        // 2. 清除徽章（如果正在聊天）
        if (isChat) {
          // const businessKey = conversation?.friend?.sessionId ?? conversation?.circle?.route;

          if (conversation?.friend?.sessionId) {
            markMessagesAsRead({
              sessionId: sessionId
            });
          } else if (conversation?.circle?.route) {
            markMessagesAsRead({
              sessionId: sessionId
            });
          }
        }

        // 3. 更新好友信息
        Object.assign(conversation, conversationItem, {
          // 如果正在聊天，保持原未读数
          ...(isChat && { unreadCount: preUnreadCount })
        });

        // 4. 如果正在聊天，不更新总未读数和未读列表
        if (isChat) return;

        // 5. 更新总未读数
        const newUnreadCount = conversation.unreadCount || 0;

        draft.totalUnreadCount = Math.max(
          0,
          (draft.totalUnreadCount || 0) + (newUnreadCount - preUnreadCount)
        );

        // 6. 更新未读列表
        if (!draft.unreadList) {
          draft.unreadList = { conversations: [], total: 0, totalUnreadCount: 0 };
        }

        if (!draft.unreadList.conversations) {
          draft.unreadList.conversations = [];
        }

        const unreadIndex = draft.unreadList.conversations.findIndex((item) => {
          if ('friend' in item) {
            return item.friend.sessionId === sessionId;
          }

          if ('circle' in item) {
            return item.circle.sessionId === sessionId;
          }

          return false;
        });

        if (newUnreadCount > 0) {
          // 有未读：更新或添加
          const updatedConversation = { ...conversation };

          if (unreadIndex > -1) {
            draft.unreadList.conversations[unreadIndex] = updatedConversation;
          } else {
            draft.unreadList.conversations.unshift(updatedConversation);
          }
        } else if (unreadIndex > -1) {
          // 无未读且存在：移除
          draft.unreadList.conversations.splice(unreadIndex, 1);
        }
      });
    },
    updateStrangerItemBySession: (sessionId, friendItem) => {
      set((draft) => {
        if (!draft.strangerList) return;

        const foundIndex = draft.strangerList.findIndex((item) => item.sessionId === sessionId);

        if (foundIndex > -1) {
          Object.assign(draft.strangerList[foundIndex], friendItem);
        }
      });
    },
    updateFriendRequestList: () => {
      set({ requestLoading: true });
      getFriendRequestList({ pageNo: 1, pageSize: 999, order: 'desc' })
        .then((res) => {
          if (res.data.code === 0) {
            set({ friendRequestList: res.data.data.list || [] });
          }
        })
        .finally(() => {
          set({ requestLoading: false });
        });
    },
    updateUnreadList: (params) => {
      const { isRefresh = false } = params || {};
      const pageSize = PAGE_SIZE;
      const pageNo = isRefresh ? 1 : get().unreadListPage;

      isRefresh && set({ unreadListPage: 1 });

      set({ requestLoading: true });
      getFriendListV2({ pageNo, pageSize, unreadOnly: true })
        .then((res) => {
          if (res.data.code !== 0) {
            throw new Error(res.data.message || 'Failed to fetch unread list');
          }

          const data = res.data.data;
          const newConversations = data.conversations || [];
          const total = data.total || 0;
          const totalUnreadCount = data.totalUnreadCount || 0;

          set((draft) => {
            if (pageNo === 1) {
              draft.unreadList = {
                conversations: newConversations,
                total,
                totalUnreadCount
              };
            } else {
              const prevConversations = draft.unreadList?.conversations || [];

              draft.unreadList = {
                conversations: [...prevConversations, ...newConversations],
                total,
                totalUnreadCount
              };
            }

            draft.totalUnreadCount = totalUnreadCount;
            draft.unreadListHasMore = (draft.unreadList.conversations.length || 0) < total;

            if (draft.unreadListHasMore) {
              draft.unreadListPage = pageNo + 1;
            }
          });
        })
        .finally(() => {
          set({ requestLoading: false });
        });
    }
  }))
);

export default useFriendStore;
